/**
 * Champs partagés des formulaires du dossier apprenant.
 *
 * L'inscription et la modification du dossier saisissent les mêmes rubriques :
 * les dupliquer ferait diverger deux écrans censés produire la même donnée —
 * c'est par là qu'une commune finit par s'écrire de deux façons.
 */
import { useRef } from 'react';
import { chiffres } from '../../lib/saisie-numeros';
import type { CommunePostale } from '../../lib/useDocuments';
import { MarqueObligatoire } from './Primitives';


export const CLASSE_CHAMP =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy focus:outline-none focus:ring-2 focus:ring-codialis-mint';

/**
 * Saisie incomplète : la bordure change, mais le champ reste acceptable. Rien
 * n'est obligatoire ici, et refuser l'enregistrement d'un numéro à moitié
 * relevé ferait perdre le reste de l'inscription.
 */
export const CLASSE_CHAMP_FAUTIF =
  'w-full rounded-lg border border-amber-400 bg-white px-3 py-2 text-sm text-codialis-navy focus:outline-none focus:ring-2 focus:ring-amber-300';

export function Section({
  titre,
  aide,
  children,
}: {
  titre: string;
  aide?: string;
  children: React.ReactNode;
}): JSX.Element {
  // Le conteneur des champs porte toujours sa propre marge haute. La variante
  // conditionnelle précédente le laissait sans marge sous le paragraphe d'aide
  // et comptait sur le `mb-2` de celui-ci : deux façons d'écrire le même
  // espacement, dont une seule survit si ce conteneur devient flex ou grid.
  return (
    <fieldset>
      <legend className="text-[11px] font-bold uppercase tracking-widest text-codialis-gray">
        {titre}
      </legend>
      {aide && <p className="mt-0.5 text-xs text-codialis-gray">{aide}</p>}
      <div className="mt-2">{children}</div>
    </fieldset>
  );
}

export function Champ({
  libelle,
  aide,
  obligatoire,
  erreur,
  children,
}: {
  libelle: string;
  aide?: string;
  obligatoire?: boolean;
  /** Saisie en cours signalée sous le champ ; remplace l'aide tant qu'elle dure. */
  erreur?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-codialis-navy">
        {libelle}
        {obligatoire && <MarqueObligatoire />}
      </span>
      {children}
      {erreur ? (
        <span className="mt-1 block text-[11px] font-medium text-amber-700">{erreur}</span>
      ) : (
        aide && <span className="mt-1 block text-[11px] text-codialis-gray">{aide}</span>
      )}
    </label>
  );
}

export function Case({
  libelle,
  aide,
  coche,
  onBasculer,
}: {
  libelle: string;
  aide: string;
  coche: boolean;
  onBasculer: () => void;
}): JSX.Element {
  return (
    <label className="flex min-h-[44px] cursor-pointer items-start gap-2 rounded-lg border border-gray-200 p-3 transition hover:bg-gray-50">
      <input
        type="checkbox"
        checked={coche}
        onChange={onBasculer}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-codialis-navy focus:ring-codialis-mint"
      />
      <span>
        <span className="block text-sm font-medium text-codialis-navy">{libelle}</span>
        <span className="block text-[11px] text-codialis-gray">{aide}</span>
      </span>
    </label>
  );
}

/**
 * Couple code postal / commune.
 *
 * Le code postal se choisit dans la liste des codes réunionnais, et la commune
 * s'écrit alors toute seule : c'est ce qui empêche « St-Denis » et « saint
 * denis » de coexister dans la même colonne d'export. Le champ reste un champ
 * de saisie, pas une liste fermée — une adresse de métropole doit pouvoir
 * s'écrire à la main, commune comprise.
 *
 * Une commune tapée à la main n'est jamais écrasée : le remplissage n'a lieu
 * que sur un champ vide ou sur une valeur que ce composant avait lui-même
 * posée. Sans cette mémoire, corriger un code postal après coup effacerait la
 * commune qu'on venait de préciser.
 */
export function CodePostalEtCommune({
  identifiant,
  communes,
  codePostal,
  commune,
  onChange,
}: {
  /** Préfixe des identifiants HTML : deux adresses coexistent dans la fenêtre. */
  identifiant: string;
  communes: CommunePostale[];
  codePostal: string;
  commune: string;
  onChange: (codePostal: string, commune: string) => void;
}): JSX.Element {
  const poseeIci = useRef('');
  const liste = `${identifiant}-codes-postaux`;
  const correspondance = communes.find((entree) => entree.codePostal === codePostal) ?? null;

  const changerCodePostal = (saisie: string): void => {
    const code = chiffres(saisie, 5);
    const trouvee = communes.find((entree) => entree.codePostal === code) ?? null;
    const modifiable = commune.trim() === '' || commune === poseeIci.current;

    if (trouvee && modifiable) {
      poseeIci.current = trouvee.commune;
      onChange(code, trouvee.commune);
      return;
    }
    onChange(code, commune);
  };

  const changerCommune = (saisie: string): void => {
    // La commune redevient une valeur à soi dès qu'elle est retouchée.
    poseeIci.current = '';
    onChange(codePostal, saisie);
  };

  return (
    <>
      <div className="sm:col-span-2 min-w-0">
        <Champ
          libelle="Code postal"
          aide={correspondance?.localite ? correspondance.localite : undefined}
        >
          <input
            list={liste}
            maxLength={5}
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="97400"
            value={codePostal}
            onChange={(evenement) => changerCodePostal(evenement.target.value)}
            className={CLASSE_CHAMP}
          />
          <datalist id={liste}>
            {communes.map((entree) => (
              <option key={entree.codePostal} value={entree.codePostal}>
                {entree.localite ? `${entree.commune} · ${entree.localite}` : entree.commune}
              </option>
            ))}
          </datalist>
        </Champ>
      </div>
      <div className="sm:col-span-4 min-w-0">
        <Champ libelle="Commune">
          <input
            maxLength={80}
            autoComplete="address-level2"
            value={commune}
            onChange={(evenement) => changerCommune(evenement.target.value)}
            className={CLASSE_CHAMP}
          />
        </Champ>
      </div>
    </>
  );
}
